import { dayjs } from '#gp/app-common/util/dayjs';
import { arrayAppend, arraySortBy } from '#gp/app-common/util/arrays';
import { sortKeyCompareOption } from '#gp/app-common/util/text-collation';

/**
 * Template list construction for the "new plan" screen, kept free of Angular so
 * it can be unit tested.
 */

export interface INewBoardTemplate {
    id: string | null;
    name: string;
    description: string | null;
    selected: boolean;
    lastEditedUTC: dayjs.Dayjs | null;
}

/**
 * The subset of IProject this module needs. Structural on purpose — importing
 * IProject would drag in projects-service, and with it the Angular module.
 */
export interface INewBoardTemplateSource {
    id: string | null;
    name: string;
    description: string | null;
    lastEditedUTC: dayjs.Dayjs | null;
}

export interface INewBoardTemplateFetchResult {
    projects: INewBoardTemplateSource[];
    succeeded: boolean;
}

export interface INewBoardTemplateLoadState {
    templates: INewBoardTemplate[];
    showTemplateList: boolean;
    autoCreateBlankPlan: boolean;
    loadFailed: boolean;
}

/** The synthetic "start from nothing" entry that always heads the list. */
export const EMPTY_TEMPLATE_NAME = "Empty";

/** Builds the list shown on the new plan screen. */
export function buildNewBoardTemplates(srcTemplates: INewBoardTemplateSource[]): INewBoardTemplate[] {

    let withEditTime = (srcTemplates || []).map(p => ({ p: p, when: p.lastEditedUTC ? p.lastEditedUTC.unix() : 0 }));

    // Sorted one key at a time, least significant first: arraySortBy is a stable
    // sort, so each pass preserves the order set by the previous one.
    const byName = arraySortBy(withEditTime, p => p.p.name, sortKeyCompareOption);
    let serverTemplates: INewBoardTemplate[] = arraySortBy(byName, p => p.when, { ...sortKeyCompareOption, direction: 'desc' })
        .map(p =>
        ({
            id: p.p.id,
            name: p.p.name,
            description: p.p.description,
            lastEditedUTC: p.p.lastEditedUTC,
            selected: false,
        }));

    const emptyTemplate: INewBoardTemplate = { id: null, name: EMPTY_TEMPLATE_NAME, description: null, lastEditedUTC: null, selected: true };

    let viewTemplates: INewBoardTemplate[] = [emptyTemplate];
    arrayAppend(viewTemplates, serverTemplates);

    return viewTemplates;
}

/**
 * True when the blank template is the only option — no choice worth asking the
 * user to make.
 */
export function isBlankTemplateOnlyChoice(templates: INewBoardTemplate[]): boolean {
    return !templates || templates.length <= 1;
}

export function buildNewBoardTemplateLoadState(fetchResult: INewBoardTemplateFetchResult): INewBoardTemplateLoadState {
    if (!fetchResult.succeeded) {
        return {
            templates: [],
            showTemplateList: false,
            autoCreateBlankPlan: false,
            loadFailed: true,
        };
    }

    const templates = buildNewBoardTemplates(fetchResult.projects);
    const blankTemplateOnly = isBlankTemplateOnlyChoice(templates);
    return {
        templates,
        showTemplateList: !blankTemplateOnly,
        autoCreateBlankPlan: blankTemplateOnly,
        loadFailed: false,
    };
}
